import { useState, useEffect } from 'react';
import Api from '../../../Services/Api';
import { loadBlacklistData, blacklistSite, unblacklistSite } from '../services/campaignService';

/**
 * Custom hook to manage site blacklist operations
 */
const useBlacklist = (alertSystem) => {
  const [blacklistedSites, setBlacklistedSites] = useState([]);
  const [blacklistItem, setBlacklistItem] = useState({ 
    subid3: null, 
    subid2: null, 
    isBlacklisted: false 
  });
  
  // Use alerts from the alert system
  const { showSuccess, showError } = alertSystem || { 
    showSuccess: () => console.log('Success alert not available'), 
    showError: () => console.error('Error alert not available') 
  };
  
  // Function to load blacklist data for a campaign
  const loadBlacklistForCampaign = async (campaignId) => {
    if (!campaignId) return;
    
    console.log(`Analyzing campaign ${campaignId} for blacklist operations`);
    
    // Improved Taboola/Facebook detection logic
    
    // Facebook campaign detection: Facebook campaign IDs are typically long numeric strings
    const campaignIdStr = String(campaignId || '');
    const isFacebookCampaignPattern = /^\d{15,}$/.test(campaignIdStr);

    // Taboola campaign detection: Either starts with T- or is a pure numeric ID (not too long)
    const isTaboolaCampaignPattern = campaignIdStr.startsWith('T-') || /^\d{1,14}$/.test(campaignIdStr);
    
    // Determine campaign type with improved logging
    if (isFacebookCampaignPattern) {
      console.log(`Campaign ${campaignId} identified as Facebook (long numeric ID pattern)`);
      console.log(`Skipping blacklist data load for Facebook campaign: ${campaignId}`);
      setBlacklistedSites([]);
      return [];
    } else if (isTaboolaCampaignPattern) {
      console.log(`Campaign ${campaignId} identified as Taboola`);

      // Extract numeric ID if needed for Taboola API
      const numericId = campaignIdStr.match(/\d+/);
      const cleanCampaignId = numericId && numericId[0] ? numericId[0] : campaignIdStr;
      
      if (cleanCampaignId !== campaignId) {
        console.log(`Using cleaned campaign ID for Taboola API: ${cleanCampaignId}`);
      }
      
      try {
        console.log(`Loading blacklist data for Taboola campaign: ${cleanCampaignId}`);
        const response = await loadBlacklistData(cleanCampaignId);
        
        if (response.success) {
          console.log(`Successfully loaded ${response.data.length} blacklisted sites for campaign ${cleanCampaignId}`);
          setBlacklistedSites(response.data);
          return response.data;
        } else {
          console.error('Failed to load blacklist data:', response.error);
          setBlacklistedSites([]);
          showError(response.error || 'Failed to load blacklist data');
          return [];
        }
      } catch (error) {
        console.error('Error in loadBlacklistForCampaign:', error);
        setBlacklistedSites([]);
        showError('Failed to load blacklist data');
        return [];
      }
    } else {
      console.log(`Campaign ${campaignId} has unknown type - will attempt to load blacklist data`);
      
      try {
        console.log(`Loading blacklist data for campaign: ${campaignId}`);
        const response = await loadBlacklistData(campaignId);
        
        if (response.success) {
          console.log(`Successfully loaded ${response.data.length} blacklisted sites`);
          setBlacklistedSites(response.data);
          return response.data;
        } else {
          console.error('Failed to load blacklist data:', response.error);
          setBlacklistedSites([]);
          showError(response.error || 'Failed to load blacklist data');
          return [];
        }
      } catch (error) {
        console.error('Error in loadBlacklistForCampaign:', error);
        setBlacklistedSites([]);
        showError('Failed to load blacklist data');
        return [];
      }
    }
  };
  
  // Function to check if a site is blacklisted
  const isBlacklisted = (subid3) => {
    return Array.isArray(blacklistedSites) && blacklistedSites.includes(subid3);
  };
  
  // Function to handle blacklist request
  const handleBlacklistRequest = async (subid3, subid2) => {
    if (!subid3 || !subid2) {
      showError('Missing campaign or site information');
      return Promise.reject(new Error('Missing campaign or site information'));
    }

    // Check if this subid3 is already in the blacklist
    const isCurrentlyBlacklisted = isBlacklisted(subid3);
    
    // Set the blacklist item state
    setBlacklistItem({
      subid3: subid3,
      subid2: subid2,
      isBlacklisted: isCurrentlyBlacklisted
    });
    
    return handleBlacklistAction(subid3, subid2, isCurrentlyBlacklisted);
  };
  
  // Function to handle blacklist action
  const handleBlacklistAction = async (subid3, subid2, isCurrentlyBlacklisted) => {
    if (!subid3 || !subid2) {
      showError('Missing campaign or site information');
      return;
    }
    
    try {
      // Immediately update UI state for a responsive feel
      if (isCurrentlyBlacklisted) {
        // Remove from blacklist in UI
        setBlacklistedSites(prev => prev.filter(site => site !== subid3));
      } else {
        // Add to blacklist in UI
        setBlacklistedSites(prev => [...prev, subid3]);
      }

      // Perform API operation
      let response;
      if (isCurrentlyBlacklisted) {
        // Remove from blacklist
        response = await unblacklistSite(subid3, subid2);
        if (response.success) {
          showSuccess(`The site ${subid3} has been successfully removed from the blacklist.`);
        }
      } else {
        // Add to blacklist
        response = await blacklistSite(subid3, subid2);
        if (response.success) {
          showSuccess(`The site ${subid3} has been successfully added to the blacklist.`);
        }
      }
      
      // Handle errors
      if (!response.success) {
        // Revert UI state on error
        if (isCurrentlyBlacklisted) {
          // Add back to blacklist
          setBlacklistedSites(prev => [...prev, subid3]);
        } else {
          // Remove from blacklist
          setBlacklistedSites(prev => prev.filter(site => site !== subid3));
        }
        
        showError(response.error || 'Error during blacklist operation');
      }
      
      // Clear the current blacklist item
      setBlacklistItem({
        subid3: null,
        subid2: null,
        isBlacklisted: false
      });
      
      return response;
    } catch (error) {
      console.error('Error during blacklist operation:', error);
      
      // Revert UI state on error
      if (isCurrentlyBlacklisted) {
        // Add back to blacklist
        setBlacklistedSites(prev => [...prev, subid3]);
      } else {
        // Remove from blacklist
        setBlacklistedSites(prev => prev.filter(site => site !== subid3));
      }
      
      showError(error.message || 'Error during blacklist operation');
      return { success: false, error: error.message };
    }
  };
  
  // Reset blacklist state
  const resetBlacklistState = () => {
    setBlacklistedSites([]);
    setBlacklistItem({
      subid3: null,
      subid2: null,
      isBlacklisted: false
    });
  };
  
  return {
    blacklistedSites,
    blacklistItem,
    isBlacklisted,
    loadBlacklistForCampaign,
    handleBlacklistRequest,
    handleBlacklistAction,
    resetBlacklistState
  };
};

export default useBlacklist; 